/**
 * Page /grc/subventions — instruction des demandes de subvention (pôle GRC).
 *
 * File par statut, prise en instruction, décision (accord/refus + montant),
 * suivi du versement (date + référence). Le RIB de l'association sert au versement.
 */

import { useEffect, useState } from 'react';
import { SubventionCampagnePanel } from '@/pages/associations/SubventionCampagnePanel';
import { api, ApiException, type ComptaPoste } from '@/lib/api';
import { GrcLayout } from '@/components/grc/GrcLayout';
import { Card, Button, Badge, Alert, LogoMark, SectionEyebrow, Field, Input } from '@/components/ui';
import { Dialog } from '@/components/ui/Dialog';
import { useToast } from '@/components/ui/Toast';
import { useConfirmDialog } from '@/lib/use-confirm-dialog';
import { Banknote, AlertCircle, Inbox, Check, X, Undo2, Download } from 'lucide-react';
import type { Subvention, SubventionStatut } from '@roronoa-collectivites/shared-types';

const BUCKETS: { key: SubventionStatut | 'tous'; label: string }[] = [
  { key: 'soumise', label: 'À instruire' },
  { key: 'en_instruction', label: 'En instruction' },
  { key: 'accordee', label: 'Accordées' },
  { key: 'versee', label: 'Versées' },
  { key: 'refusee', label: 'Refusées' },
  { key: 'tous', label: 'Toutes' },
];

function euro(n: number | null): string { return n == null ? '—' : `${n.toLocaleString('fr-FR')} €`; }
function frDate(iso: string | null): string { return iso ? new Date(iso).toLocaleDateString('fr-FR') : '—'; }
function statutBadge(s: SubventionStatut) {
  const map: Record<SubventionStatut, { v: 'success' | 'warning' | 'urgent' | 'info'; l: string }> = {
    brouillon: { v: 'info', l: 'Brouillon' }, soumise: { v: 'warning', l: 'À instruire' },
    en_instruction: { v: 'warning', l: 'En instruction' }, accordee: { v: 'success', l: 'Accordée' },
    refusee: { v: 'urgent', l: 'Refusée' }, versee: { v: 'success', l: 'Versée' },
  };
  const m = map[s];
  return <Badge variant={m.v}>{m.l}</Badge>;
}

export function GrcSubventionsPage() {
  const { toast } = useToast();
  const { confirm, dialogElement } = useConfirmDialog();
  const [items, setItems] = useState<Subvention[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [vue, setVue] = useState<'demandes' | 'campagne'>('demandes');
  const [bucket, setBucket] = useState<SubventionStatut | 'tous'>('soumise');
  const [postes, setPostes] = useState<ComptaPoste[]>([]);

  const [cible, setCible] = useState<Subvention | null>(null);
  const [montant, setMontant] = useState('');
  const [motif, setMotif] = useState('');
  const [verseLe, setVerseLe] = useState('');
  const [ref, setRef] = useState('');
  const [posteId, setPosteId] = useState('');
  const [busy, setBusy] = useState(false);

  async function charger() {
    setLoading(true); setError(null);
    try { setItems(await api.associations.subventions.list(bucket === 'tous' ? undefined : bucket)); }
    catch (e) { setError(e instanceof ApiException ? e.message : 'Erreur de chargement'); }
    finally { setLoading(false); }
  }
  useEffect(() => { charger(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [bucket]);
  // Postes comptables actifs, pour la ventilation du versement (Lot 1).
  useEffect(() => {
    api.comptabilite.postes.list()
      .then((r) => setPostes(r.postes.filter((p) => p.actif)))
      .catch(() => setPostes([]));
  }, []);

  function ouvrir(s: Subvention) {
    setCible(s); setMontant(s.montantAccorde != null ? String(s.montantAccorde) : String(s.montantDemande));
    setMotif(s.motifDecision ?? ''); setVerseLe(s.verseLe ?? ''); setRef(s.referencePaiement ?? '');
    setPosteId(s.posteId ?? '');
  }
  async function rafraichirCible(updated: Subvention) { setCible(updated); await charger(); }

  async function instruire() {
    if (!cible) return; setBusy(true);
    try { await rafraichirCible(await api.associations.subventions.instruire(cible.id)); toast.success('Prise en instruction'); }
    catch (e) { toast.error(e instanceof ApiException ? e.message : 'Erreur'); } finally { setBusy(false); }
  }
  async function decider(decision: 'accordee' | 'refusee') {
    if (!cible) return;
    if (decision === 'refusee' && !motif.trim()) { toast.error('Indiquez un motif de refus.'); return; }
    setBusy(true);
    try {
      const m = decision === 'accordee' ? Number(montant) : undefined;
      await rafraichirCible(await api.associations.subventions.decision(cible.id, decision, m, motif.trim() || undefined));
      toast.success(decision === 'accordee' ? 'Subvention accordée' : 'Demande refusée');
    } catch (e) { toast.error(e instanceof ApiException ? e.message : 'Erreur'); } finally { setBusy(false); }
  }
  async function versement() {
    if (!cible) return;
    if (!verseLe) { toast.error('Indiquez la date de versement.'); return; }
    if (!posteId) { toast.error('Choisissez un poste comptable pour la ventilation.'); return; }
    setBusy(true);
    try {
      await rafraichirCible(await api.associations.subventions.versement(cible.id, verseLe, posteId, ref.trim() || undefined));
      toast.success('Versement enregistré — décaissement créé');
    } catch (e) { toast.error(e instanceof ApiException ? e.message : 'Erreur'); } finally { setBusy(false); }
  }
  async function annulerVersement() {
    if (!cible) return;
    const ok = await confirm({
      title: 'Annuler ce versement ?',
      description: 'Un contre-passement horodaté sera enregistré (le décaissement d\'origine est conservé). La subvention repassera au statut « accordée ».',
      confirmLabel: 'Annuler le versement', danger: true,
    });
    if (!ok) return;
    setBusy(true);
    try {
      await rafraichirCible(await api.associations.subventions.annulerVersement(cible.id));
      toast.success('Versement annulé (contre-passement enregistré)');
    } catch (e) { toast.error(e instanceof ApiException ? e.message : 'Erreur'); } finally { setBusy(false); }
  }

  async function exportScdl(format: 'csv' | 'json') {
    try {
      if (format === 'csv') await api.associations.subventions.exportScdlCsv();
      else await api.associations.subventions.exportScdlJson();
      toast.success(`Export SCDL (${format.toUpperCase()}) téléchargé`);
    } catch (e) { toast.error(e instanceof ApiException ? e.message : 'Erreur d\'export'); }
  }

  return (
    <GrcLayout active="dossiers">
      <main id="main-content" className="mx-auto max-w-3xl px-4 py-8 sm:px-6 sm:py-10 animate-fade-in">
        <div className="mb-6 flex items-center gap-3">
          <LogoMark icon={Banknote} accent="grc" size="lg" />
          <div>
            <SectionEyebrow as="div">Vie associative</SectionEyebrow>
            <h1 className="mt-0.5 font-display text-xl font-semibold tracking-r-snug text-ink sm:text-2xl">Subventions</h1>
          </div>
        </div>

        {error && <Alert variant="error" icon={AlertCircle} className="mb-3">{error}</Alert>}

        {/*
          Onglet « Campagne » (item 9.6) : le maire y règle les dates limites de
          dépôt et le délai de compte rendu financier. Séparé de la file des
          demandes, qui est le travail quotidien d'instruction.
        */}
        <div className="mb-4 inline-flex rounded-md ring-1 ring-line">
          <button type="button" onClick={() => setVue('demandes')}
            className={`rounded-l-md px-3 py-1.5 text-sm ${vue === 'demandes' ? 'bg-grc text-white' : 'text-ink-muted hover:text-ink'}`}>
            Demandes
          </button>
          <button type="button" onClick={() => setVue('campagne')}
            className={`rounded-r-md px-3 py-1.5 text-sm ${vue === 'campagne' ? 'bg-grc text-white' : 'text-ink-muted hover:text-ink'}`}>
            Campagne & relances
          </button>
        </div>

        {vue === 'campagne' ? <SubventionCampagnePanel /> : (<>

        <div className="mb-4 flex flex-wrap gap-1.5">
          {BUCKETS.map((b) => (
            <button key={b.key} type="button" onClick={() => setBucket(b.key)}
              className={['inline-flex h-9 items-center rounded-r-pill px-4 text-sm font-medium transition-all',
                bucket === b.key ? 'bg-grc text-white shadow-soft' : 'text-ink-muted hover:bg-surface/70'].join(' ')}>
              {b.label}
            </button>
          ))}
        </div>

        <Card className="mb-4">
          <div className="flex flex-col gap-2 p-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <div className="text-sm font-medium text-ink">Transparence — export SCDL</div>
              <div className="text-xs text-ink-subtle">Données essentielles des subventions versées (format réglementaire OpenDataFrance).</div>
            </div>
            <div className="flex gap-2">
              <Button variant="secondary" onClick={() => exportScdl('csv')}><Download className="h-4 w-4" /> CSV</Button>
              <Button variant="secondary" onClick={() => exportScdl('json')}><Download className="h-4 w-4" /> JSON</Button>
            </div>
          </div>
        </Card>

        {loading ? <p className="text-sm text-ink-muted">Chargement…</p> : !items || items.length === 0 ? (
          <Card><div className="flex flex-col items-center gap-3 p-8 text-center"><Inbox className="h-8 w-8 text-ink-subtle" /><div className="text-sm text-ink-muted">Aucune demande dans cette catégorie.</div></div></Card>
        ) : (
          <div className="space-y-2">
            {items.map((s) => (
              <Card key={s.id}>
                <button type="button" onClick={() => ouvrir(s)} className="flex w-full items-center justify-between gap-3 p-3 text-left transition-colors hover:bg-surface/60">
                  <div className="min-w-0">
                    <div className="truncate font-medium text-ink">{s.associationNom} — {s.objet}</div>
                    <div className="text-xs text-ink-subtle">
                      {s.nature === 'projet' ? 'Projet' : 'Fonctionnement'} · exercice {s.exercice} · demandé {euro(s.montantDemande)}
                      {s.montantAccorde != null && ` · accordé ${euro(s.montantAccorde)}`}
                    </div>
                  </div>
                  {statutBadge(s.statut)}
                </button>
              </Card>
            ))}
          </div>
        )}

        </>)}
      </main>

      {/* Dialog instruction / décision / versement */}
      <Dialog open={!!cible} onClose={() => { setCible(null); charger(); }} title={cible ? `Subvention — ${cible.associationNom}` : 'Subvention'} size="md">
        {cible && (
          <div className="space-y-4">
            <div className="flex items-center gap-2 text-sm">{statutBadge(cible.statut)}</div>
            <div className="rounded-r-md border border-border bg-surface/40 p-3 text-sm">
              <div className="font-medium text-ink">{cible.objet}</div>
              <div className="mt-1 text-xs text-ink-subtle">
                {cible.nature === 'projet' ? 'Projet' : 'Fonctionnement global'} · exercice {cible.exercice}<br />
                Montant demandé : <b>{euro(cible.montantDemande)}</b>
                {cible.montantAccorde != null && <> · accordé : <b>{euro(cible.montantAccorde)}</b></>}
              </div>
              {cible.description && <p className="mt-2 text-xs text-ink-muted">{cible.description}</p>}
              {cible.verseLe && <div className="mt-2 text-xs text-success">Versée le {frDate(cible.verseLe)}{cible.referencePaiement ? ` · réf. ${cible.referencePaiement}` : ''}</div>}
            </div>

            {cible.statut === 'soumise' && (
              <Button variant="secondary" onClick={instruire} disabled={busy}>Prendre en instruction</Button>
            )}

            {(cible.statut === 'soumise' || cible.statut === 'en_instruction') && (
              <div className="space-y-2 border-t border-border pt-3">
                <div className="text-sm font-medium text-ink">Décision</div>
                <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                  <Field label="Montant accordé (€)" htmlFor="sub-montant"><Input id="sub-montant" type="number" value={montant} onChange={(e) => setMontant(e.target.value)} /></Field>
                  <Field label="Motif (si refus)" htmlFor="sub-motif"><Input id="sub-motif" value={motif} onChange={(e) => setMotif(e.target.value)} /></Field>
                </div>
                <div className="flex justify-end gap-2">
                  <Button variant="ghost" onClick={() => decider('refusee')} disabled={busy} className="text-urgent hover:bg-urgent/10 hover:text-urgent"><X className="h-4 w-4" /> Refuser</Button>
                  <Button variant="primary" onClick={() => decider('accordee')} disabled={busy}><Check className="h-4 w-4" /> Accorder</Button>
                </div>
              </div>
            )}

            {cible.statut === 'accordee' && (
              <div className="space-y-2 border-t border-border pt-3">
                <div className="text-sm font-medium text-ink">Versement (RIB de l'association)</div>
                <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                  <Field label="Date de versement" htmlFor="sub-verse"><Input id="sub-verse" type="date" value={verseLe} onChange={(e) => setVerseLe(e.target.value)} /></Field>
                  <Field label="Référence de paiement" htmlFor="sub-ref"><Input id="sub-ref" value={ref} onChange={(e) => setRef(e.target.value)} placeholder="ex. VIR-2026-001" /></Field>
                </div>
                <Field label="Poste comptable (ventilation)" htmlFor="sub-poste">
                  <select id="sub-poste" value={posteId} onChange={(e) => setPosteId(e.target.value)}
                    className="h-10 w-full rounded-r-md border border-border bg-surface px-3 text-sm text-ink focus:border-grc focus:outline-none focus:ring-2 focus:ring-grc/30">
                    <option value="">— Choisir un poste —</option>
                    {postes.map((p) => <option key={p.id} value={p.id}>{p.libelle}</option>)}
                  </select>
                </Field>
                {postes.length === 0 && (
                  <p className="text-xs text-warning">Aucun poste comptable actif. Créez-en un dans le module Comptabilité avant de verser.</p>
                )}
                <p className="text-xs text-ink-subtle">Le versement crée automatiquement un décaissement ventilé sur ce poste.</p>
                <div className="flex justify-end"><Button variant="primary" onClick={versement} disabled={busy || postes.length === 0}>Marquer versée</Button></div>
              </div>
            )}

            {cible.statut === 'versee' && (
              <div className="space-y-2 border-t border-border pt-3">
                <div className="text-sm font-medium text-ink">Décaissement comptable</div>
                <p className="text-xs text-ink-muted">
                  {cible.decaissementId
                    ? 'Un décaissement est rattaché à cette subvention. L\'annulation crée un contre-passement horodaté ; l\'écriture d\'origine est conservée.'
                    : 'Cette subvention est versée sans décaissement rattaché.'}
                </p>
                {cible.decaissementId && (
                  <div className="flex justify-end">
                    <Button variant="ghost" onClick={annulerVersement} disabled={busy} className="text-urgent hover:bg-urgent/10 hover:text-urgent">
                      <Undo2 className="h-4 w-4" /> Annuler le versement
                    </Button>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </Dialog>
      {dialogElement}
    </GrcLayout>
  );
}
